<template>
  <div
    class="left-0 top-0 w-full bg-white transition-all duration-200 ease-in-out"
    :class="isFixed ? 'shadow-sm' : ''"
  >
    <div class="mx-auto my-4 max-w-7xl py-5 lg:py-0">
      <ul
        class="flex flex-col lg:flex-row lg:items-center lg:divide-x lg:divide-shade-900"
      >
        <li
          class="hidden transition-colors duration-200 ease-in-out first:pl-0 hover:text-primary-400 lg:inline-block lg:p-5"
        >
          <MiscLogoIrcam class="h-11" />
        </li>
        <li
          v-for="(item, i) in menu"
          :key="i"
          ref="menuItems"
          class="flex items-center px-6 py-3 transition-colors duration-200 ease-in-out first:pl-0 last:pr-0 hover:!border-primary-400 lg:inline lg:p-5 lg:py-6"
          :class="item.customClass"
          @click.stop="toggleDropdown(i)"
        >
          <component
            :is="item.icon"
            v-if="item.icon"
            class="mr-4 h-6 w-6 shrink-0 lg:hidden"
          />
          <NuxtLink
            :to="{ path: localePath(item.href), hash: item.hash }"
            class="lg:text-md w-full text-mobile-lg capitalize lg:py-6 hover:text-primary-400"
          >
            {{ $t(item.label) }}
          </NuxtLink>
          <ul
            v-if="i === openIndex && item.children && item.children.length > 0"
            class="absolute bg-white mt-6 -ml-5 z-10"
          >
            <li
              v-for="(child, index) in item.children"
              :key="index"
              class="py-3 hover:text-primary-400 border border-black hover:bg-orange-100 bg-orange-50"
            >
              <NuxtLink
                :to="localePath(child.href)"
                class="lg:text-md w-full text-mobile-lg lg:py-6"
                @click.stop="closeDropdown"
              >
                <div class="mx-4">{{ $t(child.label) }}</div>
              </NuxtLink>
            </li>
          </ul>
        </li>
      </ul>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { onMounted, onBeforeUnmount, ref, watch } from 'vue'

const { y } = useWindowScroll()

const isFixed = ref(false)
const openIndex = ref<number | undefined>(undefined)
const menuItems = ref([])

defineEmits<{
  (e: 'menuitemClicked'): void
}>()

watch(
  () => y.value,
  (value) => {
    isFixed.value = value >= 40
  }
)

onMounted(() => {
  if (y.value >= 40) {
    isFixed.value = true
  }

  document.addEventListener('click', handleClickOutside)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside)
})

const handleClickOutside = (event: MouseEvent) => {
  if (
    !menuItems.value.some((menuItem: HTMLElement) =>
      menuItem.contains(event.target as Node)
    )
  ) {
    openIndex.value = undefined
  }
}

const toggleDropdown = (i: number) => {
  openIndex.value = openIndex.value === i ? undefined : i
}

const closeDropdown = () => {
  openIndex.value = undefined
}

const localePath = useLocalePath()

const menu = ref<BaseMenu>([
  {
    label: 'menu.home',
    href: '/',
    icon: markRaw(
      defineAsyncComponent(
        () => import(`~/assets/icon/documents/Document.svg` /* @vite-ignore */)
      )
    ),
  },
  {
    label: 'menu.collections',
    icon: markRaw(
      defineAsyncComponent(
        () => import(`~/assets/icon/documents/Archive.svg` /* @vite-ignore */)
      )
    ),
    children: [
      {
        label: 'menu.analysis',
        href: 'analyses',
      },
      {
        label: 'Sidney',
        href: '/sidney',
      },
      {
        label: 'menu.work_collection',
        href: '/set/images-d-une-oeuvre',
      },
      {
        label: 'menu.ircam_job',
        href: "/collection/les-metiers-de-l'ircam",
      },
    ],
  },
  {
    label: 'menu.media_library',
    href: '/',
    hash: '#media_library',
    icon: markRaw(
      defineAsyncComponent(
        () => import(`~/assets/icon/general/Mail.svg` /* @vite-ignore */)
      )
    ),
  },
  {
    label: 'menu.latest_publications',
    href: '/',
    hash: '#latest_publications',
    icon: markRaw(
      defineAsyncComponent(
        () => import(`~/assets/icon/general/Mail.svg` /* @vite-ignore */)
      )
    ),
  },
  // {
  //   label: 'menu.works_index',
  //   href: '/index-items/works',
  //   icon: markRaw(
  //     defineAsyncComponent(
  //       () => import(`~/assets/icon/general/Mail.svg` /* @vite-ignore */)
  //     )
  //   ),
  // },
  {
    label: 'menu.composers_index',
    href: '/index-items/composers',
    icon: markRaw(
      defineAsyncComponent(
        () => import(`~/assets/icon/general/Mail.svg` /* @vite-ignore */)
      )
    ),
  },
  {
    label: 'menu.contact',
    href: '/contact',
    icon: markRaw(
      defineAsyncComponent(
        () => import(`~/assets/icon/general/Mail.svg` /* @vite-ignore */)
      )
    ),
  },
  {
    label: 'menu.login',
    href: 'https://www.ircam.fr/',
    icon: markRaw(
      defineAsyncComponent(
        () => import(`~/assets/icon/general/Human.svg` /* @vite-ignore */)
      )
    ),
    customClass: 'lg:hidden',
  },
])
</script>
